import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Plane, ChevronRight, ChevronLeft, AlertCircle, Check, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { airports } from '../data/mock';
import Footer from '../components/Footer';
import FlightCard from '../components/FlightCard';
import { fetchAirlineLogos } from '../utils/airlineLogoCache';
import { deriveCodeshare } from '../utils/tripjackDerive';
import { rememberCurrentSearchUrl } from '../utils/lastSearchUrl';
import { tripBaggageBySegmentFromPriceInfo } from '../utils/tripjackTripBaggage';
import { publicFareTypeLabel } from '../utils/acfFareDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Parse `legs=BOM-DEL-2026-03-01,DEL-BLR-2026-03-05` → [{origin, destination, travel_date}] */
const parseLegsParam = (raw) => {
  if (!raw) return [];
  return raw
    .split(',')
    .map((chunk) => {
      const parts = chunk.trim().split('-');
      if (parts.length < 5) return null;
      // First 3 chars = origin, next 3 = destination, remainder = YYYY-MM-DD
      const [origin, destination, y, m, d] = parts;
      if (!origin || !destination || !y || !m || !d) return null;
      return {
        origin: origin.toUpperCase(),
        destination: destination.toUpperCase(),
        travel_date: `${y}-${m}-${d}`,
      };
    })
    .filter(Boolean);
};

/** Transform a single tripInfo bucket (positional key '0', '1', ...) into FlightCard-shaped objects. */
const transformTripjackLeg = (tripInfos = []) => {
  const out = tripInfos.map((trip, index) => {
    const firstSeg = trip.sI?.[0] || {};
    const lastSeg = trip.sI?.[trip.sI.length - 1] || {};
    const firstFd = firstSeg.fD || {};
    const codeshare = deriveCodeshare(firstFd);

    const fareOptions = (trip.totalPriceList || []).map((pi) => {
      const ad = pi.fd?.ADULT || {};
      return {
        priceId: pi.id,
        price: Math.round(ad.fC?.TF || 0),
        baseFare: Math.round(ad.fC?.BF || 0),
        taxes: Math.round(ad.fC?.TAF || 0),
        baggage: ad.bI?.iB || '15 Kg',
        cabinBaggage: ad.bI?.cB || '7 Kg',
        seatsLeft: ad.sR || null,
        mealIncluded: ad.mI || false,
        refundableType: ad.rT,
        fareType: publicFareTypeLabel(pi.fareIdentifier),
        classOfBooking: ad.cB || 'Economy',
        tripBaggageBySegmentId: tripBaggageBySegmentFromPriceInfo(pi) || undefined,
      };
    });

    const primary = fareOptions[0] || {};
    const totalMin = (trip.sI || []).reduce((s, seg) => s + (seg.duration || 0), 0);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;

    return {
      id: primary.priceId || `flight-${index}`,
      priceId: primary.priceId,
      airline: firstSeg.fD?.aI?.name || 'Unknown Airline',
      airlineCode: firstSeg.fD?.aI?.code || 'XX',
      flightNumber: firstSeg.fD?.fN || 'N/A',
      departureTime: firstSeg.dt?.split('T')[1]?.substring(0, 5) || 'N/A',
      arrivalTime: lastSeg.at?.split('T')[1]?.substring(0, 5) || 'N/A',
      from: firstSeg.da?.code || '',
      to: lastSeg.aa?.code || '',
      fromCity: firstSeg.da?.city || firstSeg.da?.code || '',
      toCity: lastSeg.aa?.city || lastSeg.aa?.code || '',
      stops: (trip.sI || []).length > 1
        ? `${(trip.sI || []).length - 1} Stop${(trip.sI || []).length > 2 ? 's' : ''}`
        : 'Non Stop',
      duration: `${h}h ${m}m`,
      price: primary.price,
      originalPrice: primary.price,
      baseFare: primary.baseFare,
      taxes: primary.taxes,
      baggage: primary.baggage,
      cabinBaggage: primary.cabinBaggage,
      seatsLeft: primary.seatsLeft,
      mealIncluded: primary.mealIncluded,
      refundableType: primary.refundableType,
      fareType: primary.fareType,
      fareOptions,
      tripBaggageBySegmentId: primary.tripBaggageBySegmentId,
      operatingAirline: codeshare.operatingAirline,
      operatingAirlineCode: codeshare.operatingAirlineCode,
      isCodeshare: codeshare.isCodeshare,
      segments: trip.sI || [],
      rawData: trip,
      source: 'tripjack',
    };
  });

  // Sort cheapest first
  return out.sort((a, b) => (a.price || 0) - (b.price || 0));
};

const LegTab = ({ leg, idx, active, selected, onClick }) => {
  const fromCity =
    airports.find((a) => a.code === leg.origin)?.city || leg.origin;
  const toCity =
    airports.find((a) => a.code === leg.destination)?.city || leg.destination;
  return (
    <button
      data-testid={`leg-tab-${idx}`}
      onClick={onClick}
      className={`flex-1 min-w-[180px] px-4 py-3 text-left border-2 rounded-xl transition-all ${
        active
          ? 'border-[#659A9B] bg-[#659A9B]/5 shadow-sm'
          : 'border-gray-200 hover:border-gray-300 bg-white'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
          Leg {idx + 1}
        </div>
        {selected ? (
          <div className="flex items-center gap-1 text-[11px] font-semibold text-green-700">
            <Check className="w-3 h-3" /> Selected
          </div>
        ) : null}
      </div>
      <div className="mt-1 flex items-center gap-1.5 text-sm font-bold text-[#1B375B]">
        <span>{leg.origin}</span>
        <ArrowRight className="w-3 h-3 text-gray-400" />
        <span>{leg.destination}</span>
      </div>
      <div className="text-[11px] text-gray-500 truncate">
        {fromCity} → {toCity}
      </div>
      <div className="text-[11px] text-gray-500 mt-0.5">{leg.travel_date}</div>
      {selected ? (
        <div className="text-[11px] text-[#659A9B] font-semibold mt-1">
          ₹{selected.price?.toLocaleString('en-IN')} · {selected.airline}
        </div>
      ) : null}
    </button>
  );
};

const MultiCitySearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const legs = useMemo(() => parseLegsParam(searchParams.get('legs')), [searchParams]);
  const adults = Number(searchParams.get('adults')) || 1;
  const children = Number(searchParams.get('children')) || 0;
  const infants = Number(searchParams.get('infants')) || 0;
  const travelClass = (searchParams.get('class') || 'Economy').toUpperCase().replace(/ /g, '_');
  const directOnly = searchParams.get('direct_only') === '1';
  const connectingOnly = searchParams.get('connecting_only') === '1';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // resultsByLeg[idx] = flight[]
  const [resultsByLeg, setResultsByLeg] = useState({});
  const [activeLeg, setActiveLeg] = useState(0);
  // selectedByLeg[idx] = flight (with selected fareOption injected as 'selected')
  const [selectedByLeg, setSelectedByLeg] = useState({});
  const [airlineLogos, setAirlineLogos] = useState({});

  useEffect(() => {
    rememberCurrentSearchUrl();
  }, [location.pathname, location.search]);

  const fetchMultiCity = useCallback(async () => {
    if (!legs || legs.length < 2) {
      setError('Multi-city search requires at least 2 legs.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/tripjack/search-multi-city`, {
        route_infos: legs,
        adults,
        children,
        infants,
        cabin_class: travelClass,
        direct_flight: directOnly,
        connecting_flight: connectingOnly,
      });
      const tripInfos = response.data?.searchResult?.tripInfos || {};
      const perLeg = {};
      legs.forEach((_, idx) => {
        const bucket =
          tripInfos[String(idx)] || tripInfos[`LEG_${idx}`] || [];
        perLeg[idx] = Array.isArray(bucket) ? transformTripjackLeg(bucket) : [];
      });
      setResultsByLeg(perLeg);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        (typeof detail === 'string' && detail) ||
        detail?.message ||
        err.message ||
        'Failed to load multi-city flights. Please try again.';
      setError(msg);
      setResultsByLeg({});
    } finally {
      setLoading(false);
    }
  }, [legs, adults, children, infants, travelClass, directOnly, connectingOnly]);

  useEffect(() => {
    fetchMultiCity();
  }, [fetchMultiCity]);

  // Preload airline logos when results arrive
  useEffect(() => {
    const codes = new Set();
    Object.values(resultsByLeg).forEach((list) =>
      (list || []).forEach((f) => f.airlineCode && codes.add(f.airlineCode))
    );
    if (codes.size === 0) return;
    fetchAirlineLogos(Array.from(codes)).then(setAirlineLogos).catch(() => {});
  }, [resultsByLeg]);

  const selectFlight = (legIdx, flight, fareOption) => {
    // Bug-fix Feb-2026 · Before this fix only `priceId`, `price`, `fareType`
    // were overridden from the chosen fare card — the rest of `flight` (built
    // from the cheapest `primary` fare on L63) leaked through via the spread.
    // BookingPage.computeFlightTotal then read `fares.adult` from the
    // cheapest fare, producing the wrong multi-pax total on the review page.
    // See SearchResults.jsx Book button for full RCA.
    const chosen = {
      ...flight,
      priceId: fareOption?.priceId || flight.priceId,
      price: fareOption?.price || flight.price,
      fareType: fareOption?.fareType || flight.fareType,
      baseFare: fareOption?.baseFare ?? flight.baseFare,
      taxes: fareOption?.taxes ?? flight.taxes,
      baggage: fareOption?.baggage ?? flight.baggage,
      cabinBaggage: fareOption?.cabinBaggage ?? flight.cabinBaggage,
      fares: fareOption?.fares || flight.fares,
      fareBreakdown: fareOption?.fareBreakdown || flight.fareBreakdown,
      tripBaggageBySegmentId: fareOption?.tripBaggageBySegmentId ?? flight.tripBaggageBySegmentId,
      seatsLeft: fareOption?.seatsLeft ?? flight.seatsLeft,
      mealIncluded: fareOption?.mealIncluded ?? flight.mealIncluded,
      refundableType: fareOption?.refundableType ?? flight.refundableType,
      originalPrice: fareOption?.price || flight.price,
      selectedFareOption: fareOption || flight.fareOptions?.[0] || null,
    };
    setSelectedByLeg((prev) => ({ ...prev, [legIdx]: chosen }));
    // Auto-advance to next unselected leg
    const nextIdx = legs.findIndex((_, i) => i !== legIdx && !selectedByLeg[i]);
    if (nextIdx > -1 && nextIdx !== legIdx) {
      setActiveLeg(nextIdx);
    }
  };

  const allSelected =
    legs.length > 0 && legs.every((_, idx) => selectedByLeg[idx]);
  const totalPrice = useMemo(
    () =>
      Object.values(selectedByLeg).reduce(
        (sum, f) => sum + (f.price || 0),
        0
      ),
    [selectedByLeg]
  );

  const handleContinueToBook = () => {
    // Collect priceIds in leg order + stash full details in localStorage for BookingPage
    const priceIds = legs.map((_, idx) => selectedByLeg[idx]?.priceId).filter(Boolean);
    if (priceIds.length !== legs.length) return;
    const selectedLegs = legs.map((leg, idx) => ({
      leg,
      flight: selectedByLeg[idx],
    }));
    try {
      localStorage.setItem(
        'multiCitySelection',
        JSON.stringify({ priceIds, selectedLegs, adults, children, infants, travelClass })
      );
    } catch {
      /* localStorage full / disabled — continue via URL */
    }
    // Navigate to booking; use a dedicated querystring so BookingPage can pick it up
    navigate(
      `/booking?multiCity=1&adults=${adults}&children=${children}&infants=${infants}&class=${travelClass}`
    );
  };

  const activeResults = resultsByLeg[activeLeg] || [];
  const cheapest = activeResults[0]?.price || 0;

  return (
    <div className="min-h-screen bg-[#F7F9FB]" data-testid="multi-city-search-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              data-testid="multi-city-back-btn"
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#1B375B]"
            >
              <ChevronLeft className="w-4 h-4" /> Modify Search
            </button>
            <div className="text-xs font-semibold text-gray-500">
              {legs.length} legs · {adults}A {children}C {infants}I · {travelClass}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Leg tabs */}
        <div className="mb-6">
          <h2 className="text-lg font-bold text-[#1B375B] mb-3">
            Pick one flight per leg
          </h2>
          <div className="flex gap-3 overflow-x-auto pb-2" data-testid="multi-city-legs-tabs">
            {legs.map((leg, idx) => (
              <LegTab
                key={`${leg.origin}-${leg.destination}-${idx}`}
                leg={leg}
                idx={idx}
                active={activeLeg === idx}
                selected={selectedByLeg[idx]}
                onClick={() => setActiveLeg(idx)}
              />
            ))}
          </div>
        </div>

        {/* Results for active leg */}
        {loading && (
          <div className="py-16 text-center" data-testid="multi-city-loading">
            <Plane className="w-8 h-8 text-[#659A9B] animate-pulse mx-auto mb-3" />
            <p className="text-sm text-gray-600">Searching flights for all {legs.length} legs…</p>
          </div>
        )}

        {!loading && error && (
          <div
            className="bg-red-50 border border-red-200 rounded-xl p-5 flex items-start gap-3"
            data-testid="multi-city-error"
          >
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-red-900 mb-1">Search failed</p>
              <p className="text-sm text-red-800">{error}</p>
              <button
                data-testid="multi-city-retry-btn"
                onClick={fetchMultiCity}
                className="mt-3 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {!loading && !error && activeResults.length === 0 && (
          <div className="py-16 text-center text-gray-500" data-testid="multi-city-empty">
            <Plane className="w-8 h-8 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No flights found for leg {activeLeg + 1}.</p>
            <p className="text-xs mt-1">Try different dates or routes.</p>
          </div>
        )}

        {!loading && !error && activeResults.length > 0 && (
          <div className="grid grid-cols-1 gap-4" data-testid={`multi-city-results-leg-${activeLeg}`}>
            {activeResults.slice(0, 20).map((flight, index) => (
              <div key={`${flight.priceId}-${index}`} className="relative">
                <FlightCard
                  flight={flight}
                  selectedFare={selectedByLeg[activeLeg]?.priceId === flight.priceId
                    ? selectedByLeg[activeLeg].selectedFareOption
                    : null}
                  index={index}
                  cheapestPrice={cheapest}
                  airlineLogos={airlineLogos}
                  isExpanded={false}
                  onToggleExpand={() => {}}
                  onBook={() => {
                    // Bug-fix Feb-2026 · If the user already picked a non-default
                    // fare card for THIS flight via onFareSelect, preserve it.
                    // Pre-fix this callback always forced flight.fareOptions[0]
                    // (cheapest), silently overriding the user's earlier pick.
                    const priorPick =
                      selectedByLeg[activeLeg]?.priceId === flight.priceId
                        ? selectedByLeg[activeLeg].selectedFareOption
                        : null;
                    selectFlight(activeLeg, flight, priorPick || flight.fareOptions?.[0]);
                  }}
                  onFareSelect={(fareOption) => selectFlight(activeLeg, flight, fareOption)}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sticky selection summary */}
      {Object.keys(selectedByLeg).length > 0 && (
        <div
          className="sticky bottom-0 bg-white border-t-2 border-[#659A9B] shadow-2xl z-20"
          data-testid="multi-city-summary-bar"
        >
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] text-gray-500 font-semibold uppercase">
                {Object.keys(selectedByLeg).length} of {legs.length} legs selected
              </p>
              <p className="text-sm font-bold text-[#1B375B]">
                Total ₹{totalPrice.toLocaleString('en-IN')}
              </p>
            </div>
            <button
              data-testid="multi-city-continue-btn"
              disabled={!allSelected}
              onClick={handleContinueToBook}
              className={`px-6 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2 transition-all ${
                allSelected
                  ? 'bg-[#659A9B] text-white hover:bg-[#547a7b] shadow-md'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
            >
              {allSelected ? 'Continue to Booking' : `Select ${legs.length - Object.keys(selectedByLeg).length} more`}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default MultiCitySearchResults;
